import PolicyAlert from '@acorn-portal/shared/src/components/alerts/Alert';
import CallButton from '@acorn-portal/shared/src/components/buttons/CallButton';
import PayNowAlerts from '@acorn-portal/shared/src/components/content/pay-now-alerts';
import RenewalOptInForm from '@acorn-portal/shared/src/components/forms/renewal-opt-in-form';
import FullPageLoadingSplash from '@acorn-portal/shared/src/components/loading/FullPageLoadingSplash';
import CommunicationModal from '@acorn-portal/shared/src/components/modal/CommunicationModal';
import PolicyPaymentScheduleCTA from '@acorn-portal/shared/src/components/paymentDetails/PolicyPaymentScheduleCTA';
import PolicyDocuments from '@acorn-portal/shared/src/components/policy/documents/policy-documents';
import InfoAlerts from '@acorn-portal/shared/src/components/policy/info-alerts';
import PolicyBreakdown from '@acorn-portal/shared/src/components/policy/policy-breakdown';
import RenewalAlerts from '@acorn-portal/shared/src/components/policy/renewal-alerts';
import PolicyStatusField from '@acorn-portal/shared/src/components/status/policyStatusField';
import { useAppDispatch } from '@acorn-portal/shared/src/hooks/reduxHook';
import usePayNowDetails from '@acorn-portal/shared/src/models/paynow-model';
import { usePolicy } from '@acorn-portal/shared/src/models/policy-model';
import { PolicyStatus } from '@acorn-portal/shared/src/services/api';
import { Product } from '@acorn-portal/shared/src/services/interface/Shared';
import {
	clearMidTermAdjustmentDetails,
	setPolicyReference,
	setPolicyToken,
	setStartDate,
} from '@acorn-portal/shared/src/store/midTermAdjustment.slice';
import { resetApiMTA } from '@acorn-portal/shared/src/store/midTermAdjustmentAPI.sclice';
import { clearMidTermAdjustmentResponseDetails } from '@acorn-portal/shared/src/store/midTermAdjustmentRatingResponse.slice';
import { clearRenewalState } from '@acorn-portal/shared/src/store/renewal.slice';
import { resetRenewalChanges } from '@acorn-portal/shared/src/store/renewalChanges.slice';
import { getInitialMTAStartDate, isProtectYD } from '@acorn-portal/shared/src/utils';
import extractBeforeDash from '@acorn-portal/shared/src/utils/extractBeforeDash';
import { getEnvProduct } from '@acorn-portal/shared/src/utils/get-env-product';
import { getPhoneNumber } from '@acorn-portal/shared/src/utils/get-phone-numbers';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { Box, CardContent, Divider, Fade, Grid, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import AcornTaxiAppCommunication from '../misc/AcornTaxiAppCommunication';
import AddOnPhoneNumbers from './components/add-on-phone-numbers';
import AutoRenewalIneligibilityContent from './components/auto-renew-eligibility-content';
import DashboardCard from './components/dashboard-card';
import CancellationJourneyAccess from './components/journey-access/cancellation/cancellation-journey-access';
import MtaJourneyAccess from './components/journey-access/mta/mta-journey-access';
import { useCancellationAccessConfig } from './hooks/journey-access/cancellation/use-cancellation-access-config';
import { useCancellationEligibilityManager } from './hooks/journey-access/cancellation/use-cancellation-eligibility-manager';
import { useCancellationPreEligibilityCheck } from './hooks/journey-access/cancellation/use-cancellation-pre-eligibility-check';
import { useMtaAccessConfig } from './hooks/journey-access/mta/use-mta-access-config';
import { getAutoRenewalEligibility } from './utils/get-auto-renewal-eligibility';
import useTrack from '@acorn-portal/shared/src/hooks/useTrack/useTrack';
import AutoRenewedBlocked from '@acorn-portal/shared/src/components/forms/auto-renew-blocked';

const PolicyDetails: FC = () => {
	const { policyToken } = useParams();
	const [searchParams] = useSearchParams();
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { loading, fullPolicy } = usePolicy(policyToken as string);
	const policyNumber = decodeURIComponent(searchParams.get('policyNumber') ?? fullPolicy.policyNumber);
	const { payNowDetails, loadingPayNow } = usePayNowDetails(policyNumber);
	const [communicationModalOpen, setCommunicationModalOpen] = useState<boolean>(false);
	const isAutoRenewalChangeBlocked = fullPolicy.isAutoRenewalChangeBlocked;
	const formattedEndDate = format(parseISO(fullPolicy.endDate), "do 'of' MMMM yyyy");
	const { track } = useTrack();

	// ** Product config **
	const claimsPhoneNumber = getPhoneNumber(fullPolicy.product, 'CLAIMS');
	const contactPhoneNumber = getPhoneNumber(fullPolicy.product, 'CONTACT');
	const renewContactNumber = getPhoneNumber(fullPolicy.product, 'AUTO_RENEW_CONTACT');
	const isTaxi = fullPolicy.product === Product.TAXI;
	const isProtectYoungDriver: boolean = isProtectYD(fullPolicy.product, fullPolicy.scheme ?? '');

	// ** Journey Access **
	// Overarching
	const activePolicy = fullPolicy.isActive;
	// MTA access
	const mtaAccessConfig = useMtaAccessConfig();
	// Cancellations access
	const { isLoading: loadingCancellationCheck } = useCancellationPreEligibilityCheck(); // calls the pre-eligibility API when base checks are met
	const cancellationEligibilityManager = useCancellationEligibilityManager(); // manages the imperative call of the eligibility API
	const cancellationAccessConfig = useCancellationAccessConfig(); // processes and returns all levels of cancellation access to drive the UI (visibility & permissions)
	// Subscription restart access
	const canAccessSubscriptionRestart = fullPolicy.isSubscriptionRestartAvailable;
	// Payment schedule access
	const canAccessPaymentSchedule =
		activePolicy && fullPolicy.isDirectDebitPaymentMethod && !fullPolicy.isShortTermPolicy;

	// ** Display **
	// Load state
	const showFullPageLoading = loading || loadingPayNow || loadingCancellationCheck;
	// Renewals
	const canOptOut = activePolicy && fullPolicy.policyStatus !== PolicyStatus.RenewalTakenUp;
	const isEndOfProduct = fullPolicy.policyStatus === PolicyStatus.EndOfProduct;
	const showRenewalContent = (canOptOut && !fullPolicy.renewalDeclined) || isEndOfProduct;
	const isActiveProtectYoungDriver = isProtectYoungDriver && !isEndOfProduct;
	const autoRenewalEligibility = getAutoRenewalEligibility(isActiveProtectYoungDriver, isEndOfProduct);
	const isStreetCover = import.meta.env.VITE_AUTH_REALM === 'StreetCover';
	const cancellationRefundEligible = !isStreetCover || !fullPolicy.isShortTermPolicy;
	// Notifications
	const company = extractBeforeDash((import.meta.env.VITE_JOURNEY ?? '').toUpperCase());
	const isTaxiFeeIncreaseEnabled = useFeatureFlagEnabled(`${getEnvProduct().toLowerCase()}_taxi_fee_increase`);
	const taxiAppNotificationEnabled = useFeatureFlagEnabled('acorn_taxi_appprompt');
	const showTaxiAdminFeeNotification = isTaxi && isTaxiFeeIncreaseEnabled;
	const showTaxiAppNotification = company === 'ACORN' && isTaxi && taxiAppNotificationEnabled;
	// Local storage
	const localStorageCommsModalKey = 'acorn-portal-communication-modal';
	const handleCommunicationModalClose = () => {
		localStorage.setItem(localStorageCommsModalKey, 'true');
		setCommunicationModalOpen(false);
	};

	// Local storage
	useEffect(() => {
		const hasDismissed = localStorage.getItem(localStorageCommsModalKey);
		if (!hasDismissed) {
			setCommunicationModalOpen(true);
		}
	}, []);

	useEffect(() => {
		track(`Policy Details Page Viewed`);
	}, []);

	// Redux (thunk)
	useEffect(() => {
		dispatch(clearRenewalState());
		dispatch(resetRenewalChanges());
		dispatch(clearMidTermAdjustmentDetails());
		dispatch(clearMidTermAdjustmentResponseDetails());
		dispatch(resetApiMTA());
		dispatch(setPolicyReference(fullPolicy.policyNumber));
		dispatch(setPolicyToken(fullPolicy.policyToken));
		dispatch(setStartDate(getInitialMTAStartDate()));
	}, [dispatch, fullPolicy.policyNumber, fullPolicy.policyToken]);

	return (
		<FullPageLoadingSplash message='Fetching Policy Details...' loading={showFullPageLoading}>
			<Fade in>
				<Box>
					<Grid container spacing={2}>
						<Grid size={{ xs: 12 }}>
							{canAccessSubscriptionRestart && (
								<Box>
									<PolicyAlert
										type='warning'
										title='Your 30 day subscription policy has expired'
										onActionClick={() => navigate(`${ROUTE_PATHS.subscriptionRestart}/${fullPolicy.policyToken}`)}
										actionText='Renew your subscription'
										hideBorder
									>
										You can renew your policy for another 30 days. Select a date up to 7 days into the future for when
										you want the policy to start and we will provide you with a new quote.
									</PolicyAlert>
								</Box>
							)}
							{showTaxiAdminFeeNotification && (
								<InfoAlerts
									infoText="From the 26th of May, our administration fees will increase for 7, 30 & 90 taxi policies. This is the fee we charge to set up and to renew your policy. <br/><br/> <b>Our new fees will be:</b><br/> <ui><li>7 day policies - £10.00</li><li>30 day policies - £20.00</li><li>90 day policies - £50.00</li></ui> <br/> To see all our fees and charges, please check our <a href='https://www.acorninsure.co.uk/docs/2025_Terms%20of%20Business_v6.5.pdf' target='_blank'>Terms of Business</a>."
									title='Our admin fees are increasing'
								/>
							)}
							<RenewalAlerts policy={fullPolicy} />
							<PayNowAlerts payNowDetails={payNowDetails.details} />
						</Grid>
						<Grid size={{ xs: 12, md: 7 }} sx={{ mb: { xs: 0, md: 4 } }}>
							{/* Policy Details Main Section - Left Side of Page */}
							<DashboardCard>
								<CardContent>
									<Typography
										component='h1'
										variant='dashboard-card-heading'
									>{`Policy Details | ${fullPolicy.policyNumber}`}</Typography>
									<PolicyStatusField policy={fullPolicy} />
									<PolicyBreakdown policy={fullPolicy} />
									{/* MTA Journey Access */}
									{activePolicy && (
										<>
											<Divider />
											<Box sx={{ m: '1rem', mb: 0 }}>
												<Typography component='h3' sx={{ fontWeight: 700 }}>
													Make a change
												</Typography>
												<Typography sx={{ my: '1rem' }}>
													Make a change to your policy, vehicle or personal information.
												</Typography>{' '}
												<MtaJourneyAccess
													handleClick={() => navigate(ROUTE_PATHS.makeAChangeHome)}
													permitAccess={mtaAccessConfig.permitted}
													showAccessMessage={mtaAccessConfig.notification.show}
													reasonForBlockedAccess={mtaAccessConfig.notification.reasonBlocked}
												/>
											</Box>
										</>
									)}
								</CardContent>
							</DashboardCard>
						</Grid>
						{/* Policy Details Accompanying Sections - Right Side of Page*/}
						<Grid size={{ xs: 12, md: 5 }} sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 2 }}>
							<DashboardCard>
								<CardContent>
									<Typography variant='dashboard-card-heading' sx={{ mb: '1rem' }}>
										Policy Documents
									</Typography>
									<PolicyDocuments />
								</CardContent>
							</DashboardCard>
							<DashboardCard>
								<CardContent>
									<Typography variant='dashboard-card-heading' sx={{ mb: '1rem' }}>
										Claims & Breakdown
									</Typography>
									<CallButton buttonLabel={'Claims: ' + claimsPhoneNumber} phoneNumber={claimsPhoneNumber} />
									<AddOnPhoneNumbers addOns={fullPolicy.addOns} product={fullPolicy.product} />
								</CardContent>
							</DashboardCard>
							{!canAccessPaymentSchedule && <PolicyPaymentScheduleCTA />}
							{showRenewalContent && (
								<DashboardCard>
									<CardContent>
										<Typography variant='dashboard-card-heading'>
											{fullPolicy.isShortTermPolicy ? 'Rolling Subscription' : 'Your Renewal'}
										</Typography>
										{fullPolicy.autoRenewal && autoRenewalEligibility.status === 'eligible' && (
											<Typography sx={{ mt: 2 }}>
												Your policy is set to auto renew on the {formattedEndDate}.
											</Typography>
										)}
										{autoRenewalEligibility.status === 'eligible' ? (
											isAutoRenewalChangeBlocked ? (
												<AutoRenewedBlocked
													isShortTermPolicy={fullPolicy.isShortTermPolicy}
													contactPhoneNumber={renewContactNumber}
													autoRenewalOptIn={fullPolicy.autoRenewal}
													expiryDate={formattedEndDate}
												/>
											) : (
												<RenewalOptInForm
													isShortTermPolicy={fullPolicy.isShortTermPolicy}
													policyToken={fullPolicy.policyToken}
													autoRenewal={fullPolicy.autoRenewal}
													expiryDate={fullPolicy.endDate}
													cancellationRefundEligible={cancellationRefundEligible}
												/>
											)
										) : (
											<AutoRenewalIneligibilityContent
												reason={autoRenewalEligibility.reason}
												contactPhoneNumber={contactPhoneNumber}
											/>
										)}
									</CardContent>
								</DashboardCard>
							)}
							{/* Cancellations Journey Access */}
							{cancellationAccessConfig.baseAccess && (
								<DashboardCard sx={{ my: 0 }}>
									<CardContent>
										<Box>
											<Typography variant='dashboard-card-heading'>Cancellations</Typography>
											<Typography sx={{ my: '1rem' }}>
												You may be able to cancel your policy if it is currently active and not within a renewal window.
											</Typography>
											<CancellationJourneyAccess
												accessConfig={cancellationAccessConfig}
												eligibilityManager={cancellationEligibilityManager}
											/>
										</Box>
									</CardContent>
								</DashboardCard>
							)}
						</Grid>
					</Grid>
				</Box>
			</Fade>
			{showTaxiAppNotification && (
				<CommunicationModal open={communicationModalOpen} onClose={handleCommunicationModalClose}>
					<AcornTaxiAppCommunication />
				</CommunicationModal>
			)}
		</FullPageLoadingSplash>
	);
};

export default PolicyDetails;
